/**
 * Visual Artifact Validation
 *
 * Minimal runtime type guards for shared visual artifact payloads.
 *
 * Primary responsibilities:
 * - Validate supported artifact discriminator values
 * - Narrow unknown values into visual artifact types
 *
 * Dependencies:
 * - Visual artifact types
 */

import type {
  AddressMapArtifact,
  AddressMapMarker,
  BlueprintArtifact,
  BlueprintBounds,
  BlueprintElement,
  BlueprintElementKind,
  BlueprintPoint,
  HeatmapArtifact,
  HeatmapBounds,
  HeatmapPoint,
  MapCoordinate,
  RouteMapArtifact,
  RouteMapRoute,
  RouteMapStop,
  SchematicArtifact,
  SchematicEdge,
  SchematicNode,
  ThreeDModelArtifact,
  ThreeDModelFormat,
  ThreeDModelUnit,
  TerritoryMapArtifact,
  TerritoryPolygon,
  VisualArtifact,
  VisualArtifactBase,
  VisualArtifactLinearUnit,
  VisualArtifactSize3D,
  VisualArtifactType
} from './types'
import { visualArtifactTypes } from './types'

const threeDModelFormats = ['glb', 'gltf', 'obj', 'ply', 'stl'] as const

const visualArtifactLinearUnits = [
  'meter',
  'centimeter',
  'millimeter',
  'inch',
  'foot'
] as const

const blueprintElementKinds = [
  'wall',
  'door',
  'window',
  'fixture',
  'dimension',
  'label',
  'area'
] as const

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0

const isOptionalNonEmptyString = (value: unknown) =>
  value === undefined || isNonEmptyString(value)

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value)

const isPositiveNumber = (value: unknown): value is number =>
  isFiniteNumber(value) && value > 0

const isNonNegativeNumber = (value: unknown): value is number =>
  isFiniteNumber(value) && value >= 0

const isOptionalNonNegativeNumber = (value: unknown) =>
  value === undefined || isNonNegativeNumber(value)

const isNumberInRange = (value: unknown, min: number, max: number): value is number =>
  isFiniteNumber(value) && value >= min && value <= max

const hasBaseFields = <Type extends VisualArtifactType>(
  value: unknown,
  type: Type
): value is VisualArtifactBase<Type, unknown> =>
  isRecord(value) &&
  value.type === type &&
  isNonEmptyString(value.id) &&
  isNonEmptyString(value.title) &&
  isNonEmptyString(value.createdAt) &&
  isRecord(value.data)

const isThreeDModelFormat = (value: unknown): value is ThreeDModelFormat =>
  typeof value === 'string' && threeDModelFormats.some((format) => format === value)

const isVisualArtifactLinearUnit = (
  value: unknown
): value is VisualArtifactLinearUnit =>
  typeof value === 'string' && visualArtifactLinearUnits.some((unit) => unit === value)

const isThreeDModelUnit = (value: unknown): value is ThreeDModelUnit =>
  isVisualArtifactLinearUnit(value)

const isOptionalThreeDModelUnit = (value: unknown) =>
  value === undefined || isThreeDModelUnit(value)

const isVisualArtifactSize3D = (value: unknown): value is VisualArtifactSize3D =>
  isRecord(value) &&
  isPositiveNumber(value.width) &&
  isPositiveNumber(value.height) &&
  isPositiveNumber(value.depth)

const isOptionalVisualArtifactSize3D = (value: unknown) =>
  value === undefined || isVisualArtifactSize3D(value)

const isSchematicNode = (value: unknown): value is SchematicNode =>
  isRecord(value) &&
  isNonEmptyString(value.id) &&
  isNonEmptyString(value.label) &&
  isOptionalNonEmptyString(value.kind) &&
  (value.x === undefined || isFiniteNumber(value.x)) &&
  (value.y === undefined || isFiniteNumber(value.y))

const isSchematicEdge = (
  value: unknown,
  nodeIds: ReadonlySet<string>
): value is SchematicEdge =>
  isRecord(value) &&
  isNonEmptyString(value.id) &&
  isNonEmptyString(value.from) &&
  isNonEmptyString(value.to) &&
  isOptionalNonEmptyString(value.label) &&
  nodeIds.has(value.from) &&
  nodeIds.has(value.to)

const isMapCoordinate = (value: unknown): value is MapCoordinate =>
  isRecord(value) &&
  isNumberInRange(value.latitude, -90, 90) &&
  isNumberInRange(value.longitude, -180, 180)

const isAddressMapMarker = (value: unknown): value is AddressMapMarker =>
  isRecord(value) &&
  isNonEmptyString(value.id) &&
  isNonEmptyString(value.label) &&
  isNonEmptyString(value.address) &&
  isMapCoordinate(value.coordinate) &&
  (value.intensity === undefined || isNumberInRange(value.intensity, 0, 1))

const isMapZoom = (value: unknown) =>
  value === undefined || isNumberInRange(value, 0, 22)

const isMapPurpose = (value: unknown) =>
  value === undefined ||
  value === 'address-map' ||
  value === 'route-map' ||
  value === 'territory-map' ||
  value === 'heatmap'

const isHeatmapBounds = (value: unknown): value is HeatmapBounds =>
  isRecord(value) && isPositiveNumber(value.width) && isPositiveNumber(value.height)

const isHeatmapPoint = (value: unknown, bounds: HeatmapBounds): value is HeatmapPoint =>
  isRecord(value) &&
  isNumberInRange(value.x, 0, bounds.width) &&
  isNumberInRange(value.y, 0, bounds.height) &&
  isNumberInRange(value.intensity, 0, 1)

const isRouteMapStop = (value: unknown): value is RouteMapStop =>
  isRecord(value) &&
  isNonEmptyString(value.id) &&
  isNonEmptyString(value.label) &&
  isMapCoordinate(value.coordinate) &&
  isOptionalNonEmptyString(value.address) &&
  isOptionalNonNegativeNumber(value.sequence)

const hasKnownRouteStops = (value: unknown, stopIds: ReadonlySet<string>) =>
  Array.isArray(value) &&
  value.length >= 2 &&
  value.every((stopId) => isNonEmptyString(stopId) && stopIds.has(stopId))

const isOptionalMapPath = (value: unknown) =>
  value === undefined ||
  (Array.isArray(value) && value.length >= 2 && value.every(isMapCoordinate))

const isRouteMapRoute = (
  value: unknown,
  stopIds: ReadonlySet<string>
): value is RouteMapRoute =>
  isRecord(value) &&
  isNonEmptyString(value.id) &&
  hasKnownRouteStops(value.stopIds, stopIds) &&
  isOptionalNonEmptyString(value.label) &&
  isOptionalMapPath(value.path) &&
  isOptionalNonNegativeNumber(value.distanceMeters) &&
  isOptionalNonNegativeNumber(value.durationSeconds) &&
  isOptionalNonEmptyString(value.color)

const isTerritoryMetric = (value: unknown) =>
  isRecord(value) &&
  isNonEmptyString(value.label) &&
  isFiniteNumber(value.value) &&
  isOptionalNonEmptyString(value.unit)

const isTerritoryPolygon = (value: unknown): value is TerritoryPolygon =>
  isRecord(value) &&
  isNonEmptyString(value.id) &&
  isNonEmptyString(value.label) &&
  Array.isArray(value.coordinates) &&
  value.coordinates.length >= 3 &&
  value.coordinates.every(isMapCoordinate) &&
  isOptionalNonEmptyString(value.owner) &&
  isOptionalNonEmptyString(value.color) &&
  (value.intensity === undefined || isNumberInRange(value.intensity, 0, 1)) &&
  (value.metric === undefined || isTerritoryMetric(value.metric))

const isBlueprintBounds = (value: unknown): value is BlueprintBounds =>
  isRecord(value) && isPositiveNumber(value.width) && isPositiveNumber(value.height)

const isBlueprintPoint = (
  value: unknown,
  bounds: BlueprintBounds
): value is BlueprintPoint =>
  isRecord(value) &&
  isNumberInRange(value.x, 0, bounds.width) &&
  isNumberInRange(value.y, 0, bounds.height)

const isBlueprintElementKind = (value: unknown): value is BlueprintElementKind =>
  typeof value === 'string' && blueprintElementKinds.some((kind) => kind === value)

const getBlueprintMinimumPoints = (kind: BlueprintElementKind) => {
  if (kind === 'area') return 3
  if (kind === 'fixture' || kind === 'label') return 1
  return 2
}

const isBlueprintElement = (
  value: unknown,
  bounds: BlueprintBounds
): value is BlueprintElement => {
  if (!isRecord(value) || !isBlueprintElementKind(value.kind)) {
    return false
  }

  return (
    isNonEmptyString(value.id) &&
    Array.isArray(value.points) &&
    value.points.length >= getBlueprintMinimumPoints(value.kind) &&
    value.points.every((point) => isBlueprintPoint(point, bounds)) &&
    isOptionalNonEmptyString(value.label) &&
    isOptionalNonNegativeNumber(value.measurement)
  )
}

export const isVisualArtifactType = (value: unknown): value is VisualArtifactType =>
  typeof value === 'string' && visualArtifactTypes.some((type) => type === value)

export const isThreeDModelArtifact = (value: unknown): value is ThreeDModelArtifact => {
  if (!hasBaseFields(value, '3d-model')) return false

  const data = value.data

  return (
    isRecord(data) &&
    isNonEmptyString(data.assetUrl) &&
    isThreeDModelFormat(data.format) &&
    isOptionalNonEmptyString(data.previewImageUrl) &&
    isOptionalThreeDModelUnit(data.unit) &&
    isOptionalVisualArtifactSize3D(data.bounds)
  )
}

export const isSchematicArtifact = (value: unknown): value is SchematicArtifact => {
  if (!hasBaseFields(value, 'schematic')) return false

  const data = value.data

  if (!isRecord(data) || !Array.isArray(data.nodes) || !Array.isArray(data.edges)) {
    return false
  }

  const nodes = data.nodes

  if (nodes.length === 0 || !nodes.every(isSchematicNode)) {
    return false
  }

  const nodeIds = new Set(nodes.map((node) => node.id))

  return data.edges.every((edge) => isSchematicEdge(edge, nodeIds))
}

export const isAddressMapArtifact = (value: unknown): value is AddressMapArtifact => {
  if (!hasBaseFields(value, 'address-map')) return false

  const data = value.data

  return (
    isRecord(data) &&
    isMapCoordinate(data.center) &&
    Array.isArray(data.markers) &&
    data.markers.length > 0 &&
    data.markers.every(isAddressMapMarker) &&
    isMapZoom(data.zoom) &&
    isMapPurpose(data.mapPurpose)
  )
}

export const isHeatmapArtifact = (value: unknown): value is HeatmapArtifact => {
  if (!hasBaseFields(value, 'heatmap')) return false

  const data = value.data

  const bounds = isRecord(data) ? data.bounds : undefined

  if (!isRecord(data) || !isHeatmapBounds(bounds) || !Array.isArray(data.points)) {
    return false
  }

  return data.points.length > 0 && data.points.every((point) => isHeatmapPoint(point, bounds))
}

export const isRouteMapArtifact = (value: unknown): value is RouteMapArtifact => {
  if (!hasBaseFields(value, 'route-map')) return false

  const data = value.data

  if (
    !isRecord(data) ||
    !isMapCoordinate(data.center) ||
    !Array.isArray(data.stops) ||
    !Array.isArray(data.routes) ||
    !isMapZoom(data.zoom)
  ) {
    return false
  }

  if (data.stops.length < 2 || !data.stops.every(isRouteMapStop)) {
    return false
  }

  const stopIds = new Set(data.stops.map((stop) => stop.id))

  return data.routes.length > 0 && data.routes.every((route) => isRouteMapRoute(route, stopIds))
}

export const isTerritoryMapArtifact = (
  value: unknown
): value is TerritoryMapArtifact => {
  if (!hasBaseFields(value, 'territory-map')) return false

  const data = value.data

  return (
    isRecord(data) &&
    isMapCoordinate(data.center) &&
    Array.isArray(data.territories) &&
    data.territories.length > 0 &&
    data.territories.every(isTerritoryPolygon) &&
    (data.markers === undefined ||
      (Array.isArray(data.markers) && data.markers.every(isAddressMapMarker))) &&
    isMapZoom(data.zoom)
  )
}

export const isBlueprintArtifact = (value: unknown): value is BlueprintArtifact => {
  if (!hasBaseFields(value, 'blueprint')) return false

  const data = value.data
  const bounds = isRecord(data) ? data.bounds : undefined

  if (
    !isRecord(data) ||
    !isVisualArtifactLinearUnit(data.unit) ||
    !isBlueprintBounds(bounds) ||
    !Array.isArray(data.elements) ||
    !isOptionalNonEmptyString(data.scale)
  ) {
    return false
  }

  return (
    data.elements.length > 0 &&
    data.elements.every((element) => isBlueprintElement(element, bounds))
  )
}

export const isVisualArtifact = (value: unknown): value is VisualArtifact =>
  isThreeDModelArtifact(value) ||
  isSchematicArtifact(value) ||
  isAddressMapArtifact(value) ||
  isHeatmapArtifact(value) ||
  isRouteMapArtifact(value) ||
  isTerritoryMapArtifact(value) ||
  isBlueprintArtifact(value)
